/**
 * `useBrowserNotifications` (F2 / decision D5) — thin wrapper around the
 * Web Notifications API that:
 *   - reports current permission (`'default' | 'granted' | 'denied' |
 *     'unsupported'`),
 *   - requests permission on demand,
 *   - fires a desktop pop-up via `notify(key, title, body)` and de-dupes
 *     by `key` PER CALENDAR DAY so the same reminder never re-fires
 *     repeatedly while the tab is open or after a refresh.
 *
 * When the browser lacks `Notification` (e.g. SSR, older browsers) the
 * hook degrades to `'unsupported'` and `notify` becomes a no-op — callers
 * still show the in-app badge/list, satisfying the "graceful fallback".
 */
import { useCallback, useEffect, useState } from 'react';

export type BrowserPermission = NotificationPermission | 'unsupported';

export interface BrowserNotifications {
  readonly permission: BrowserPermission;
  readonly request: () => Promise<BrowserPermission>;
  /** Fire a pop-up if granted; de-duped by `key` for the current day. */
  readonly notify: (key: string, title: string, body: string) => void;
  readonly supported: boolean;
}

function todayKey(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function firedStorageKey(): string {
  return `reefapp.notifications.fired.${todayKey()}`;
}

function readFired(): Set<string> {
  try {
    const raw = localStorage.getItem(firedStorageKey());
    if (raw === null) return new Set();
    const arr = JSON.parse(raw) as unknown;
    return Array.isArray(arr) ? new Set(arr.filter((x): x is string => typeof x === 'string')) : new Set();
  } catch {
    return new Set();
  }
}

function writeFired(fired: Set<string>): void {
  try {
    localStorage.setItem(firedStorageKey(), JSON.stringify([...fired]));
  } catch {
    // Quota / private mode — non-fatal; at worst a reminder re-fires once.
  }
}

function currentPermission(): BrowserPermission {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission;
}

export function useBrowserNotifications(): BrowserNotifications {
  const supported = typeof Notification !== 'undefined';
  const [permission, setPermission] = useState<BrowserPermission>(() =>
    currentPermission(),
  );

  // Keep permission in sync if it changed in another tab / via the UI.
  useEffect(() => {
    setPermission(currentPermission());
  }, []);

  const request = useCallback(async (): Promise<BrowserPermission> => {
    if (typeof Notification === 'undefined') return 'unsupported';
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      return result;
    } catch {
      // Some browsers throw on the legacy callback form — treat as denied.
      const fallback = currentPermission();
      setPermission(fallback);
      return fallback;
    }
  }, []);

  const notify = useCallback(
    (key: string, title: string, body: string): void => {
      if (typeof Notification === 'undefined') return;
      if (Notification.permission !== 'granted') return;
      const fired = readFired();
      if (fired.has(key)) return;
      fired.add(key);
      writeFired(fired);
      try {
        // The constructor's side effect shows the pop-up; we assign + void
        // the instance so we neither trip `no-new` nor keep an unused ref.
        const popup = new Notification(title, { body, tag: key });
        void popup;
      } catch {
        // Constructing can throw on some platforms — the in-app badge
        // remains the primary surface, so swallow.
      }
    },
    [],
  );

  return { permission, request, notify, supported };
}
